// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';
import styled from 'styled-components';

import { Target } from '../../client';
import { ExpandedBetGroupContext } from '../../contexts/ExpandedBetGroupContext';
import { getAppDOMElement } from '../../DOMElements';
import { ReactHeight } from '../../lib/react-height.custom';
import { SpecialBetGroups } from '../../types/bet';
import { MatchViewModes } from '../../types/matchView';
import { type MatchViewMode } from '../../types/matchView';
import useWinabetLibContext from '../../winabet-lib/hooks/useWinabetLibContext';
import BetGroupFactoryContainer from '../BetGroupFactory/BetGroupFactoryContainer';
import TopMyMatchContainer from '../MatchTopMyMatch/MatchTopMyMatchContainer';
import WinaLoader from '../WinaLoader';

const getElementHeight = (el) => el.offsetHeight;

const overscanIndicesGetter = (options) => {
  const { cellCount, overscanCellsCount, scrollDirection, startIndex, stopIndex } = options;

  let overscanStartIndex = 0;
  let overscanStopIndex = 0;
  switch (scrollDirection) {
    case 1:
    case -1:
      overscanStartIndex = startIndex - overscanCellsCount;
      overscanStopIndex = stopIndex + overscanCellsCount;
      if (overscanStartIndex < 0) {
        overscanStopIndex += Math.abs(overscanStartIndex);
      }
      break;
    default:
      break;
  }
  return {
    overscanStartIndex: Math.max(0, overscanStartIndex),
    overscanStopIndex: Math.min(cellCount - 1, overscanStopIndex),
  };
};

const Wrap = styled.div`
  padding: 0 10px;

  .ReactVirtualized__Grid,
  .ReactVirtualized__Grid__innerScrollContainer {
    overflow: visible !important;
    outline: none !important;
  }
`;

const StyledBetGroupFactoryContainer = styled(BetGroupFactoryContainer)`
  .bet-group-line {
    margin: 0 0.6em;
  }

  .expand-button {
    margin: 0.6em;
  }
`;

type MatchGroupsProps = {
  className?: string;
  matchId: number;
  betGroups: {
    [key: number | string]: {
      betIds: Array<number>;
      title: string;
      helperText: string;
    };
  };
  matchViewMode?: MatchViewMode;
  order: Array<number | string>;
  orderIndexToAutoScroll: number;
  pathname: string;
};

const MatchGroups = ({
  matchId,
  betGroups,
  order,
  matchViewMode = MatchViewModes.CLASSIC,
  orderIndexToAutoScroll,
  pathname,
  className,
}: MatchGroupsProps) => {
  const cache = useRef({});
  const renderListRef = useRef();
  const winabetLibContext = useWinabetLibContext();
  const [intialIndexToAutoScroll, setIntialIndexToAutoScroll] = useState(orderIndexToAutoScroll ?? undefined);

  const [expandedBetGroups, setExpandedBetGroups] = useState({});

  const scrollElement = useMemo(() => {
    if (Target.__LIBRARY__) {
      return winabetLibContext?.parentRef.current;
    }

    if (Target.__MOBILE__) {
      return getAppDOMElement();
    }

    return window;
  }, [winabetLibContext]);

  const rowHeight = ({ index }) => {
    return cache.current[index] || 0;
  };

  const getContent = useCallback(
    (index) => {
      const groupId = order[index];
      const betGroup = betGroups[groupId];

      if (betGroup === SpecialBetGroups.TOP_MY_MATCHS) {
        return <TopMyMatchContainer key={matchId} matchId={matchId} />;
      }

      if (betGroup && typeof betGroup === 'object') {
        const { title, helperText, betsData } = betGroup;

        return (
          <StyledBetGroupFactoryContainer
            expandId={title}
            key={groupId}
            matchId={matchId}
            betsData={betsData}
            title={title}
            helperText={helperText}
          />
        );
      }
    },
    [betGroups, matchId, order],
  );

  const renderRow = useCallback(
    ({ index, key, style, width }) => {
      const content = getContent(index);

      return (
        <Wrap className={className} style={style} key={key}>
          <ReactHeight
            getElementHeight={getElementHeight}
            onHeightReady={(height) => {
              cache.current[index] = height;
              if (renderListRef.current) {
                renderListRef.current.recomputeRowHeights(index);
              }
            }}
            style={{ width }}
          >
            {content}
            <ReactResizeDetector
              handleHeight
              onResize={(_, height) => {
                cache.current[index] = height;
                if (renderListRef.current) {
                  renderListRef.current.recomputeRowHeights(index);
                }
              }}
            />
          </ReactHeight>
        </Wrap>
      );
    },
    [className, getContent],
  );

  useEffect(() => {
    let pid = setTimeout(() => {
      setIntialIndexToAutoScroll(undefined);
      pid = null;
    }, 0);

    return () => {
      if (pid) {
        clearTimeout(pid);
      }
    };
  }, []);

  const forwardedState = useMemo(
    () => ({
      expandedBetGroups,
      setExpandedBetGroups,
      expandOnMount: Object.keys(betGroups).length === 1,
    }),
    [expandedBetGroups, setExpandedBetGroups, betGroups],
  );

  if (order.length) {
    // On ajoute la key=pathname pour forcer le re-render du composant lors d'un changement de page
    // Dans le cas de l'ouverture des lineups, il y a un display:none sur les elements de la liste car
    // on ne veut pas les afficher pour optimiser les performances.
    // Seulement lorsqu'on ferme les lineup, ca enleve le display none, mais le composent scroller ne se
    // re-rend pas. Du coup les bets sont toujours masqués. Issue #3293
    return (
      <ExpandedBetGroupContext.Provider value={forwardedState}>
        <WindowScroller key={pathname} scrollElement={scrollElement}>
          {({ height, scrollTop, onChildScroll }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  ref={renderListRef}
                  autoHeight
                  height={height || 0}
                  width={width || 0}
                  rowCount={order.length}
                  overscanRowCount={Target.__MOBILE__ ? 2 : 10}
                  overscanIndicesGetter={overscanIndicesGetter}
                  rowHeight={rowHeight}
                  rowRenderer={renderRow}
                  scrollToIndex={intialIndexToAutoScroll}
                  scrollToAlignment="start"
                  scrollTop={scrollTop}
                  items={order}
                  onScroll={onChildScroll}
                />
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      </ExpandedBetGroupContext.Provider>
    );
  }

  if (matchViewMode === MatchViewModes.MY_MATCH) {
    return <WinaLoader />;
  }

  return null;
};
export default MatchGroups;
